import React, { useRef, useState } from 'react';
import { Tab, Tabs, Typography } from '@mui/material';
import Notification from '../../../../../../../../common/Styleguide/Common/Notification';
import moment from 'moment';
import { useQuery } from '@apollo/client';
import {
  MY_GAME_DAILY_STATS_QUERY,
  MY_GAME_STATS_QUERY,
  MyGameDailyStatsQuery,
  MyGameStatsQuery,
  Submission,
} from '../../../submission-graphql';
import {
  getDailyLabels,
  getDailyRawData,
  isPastByNDays,
  MetricsCategories,
  DeviceFilterOptionsValues,
  MetricsIds,
  MetricsTabs,
  renderDeviceFilterLabelHelper,
  setDefaultSelectedTab,
  sumArrayValues,
  DEFAULT_TIMEFRAME_FILTER,
  MetricsTimeframeInput,
  getAggregatedAnalytics,
  isAfterDate,
} from '../../../../../../../helpers/metricsHelpers';
import { MetricCards } from '../../../../../../../../common/Styleguide/Common/metricCard.styles';
import { GraphCards } from '../../../../../../../../common/Styleguide/Common/graphCard.styles';
import MetricCard from '../../../../../../../../common/Styleguide/Common/MetricCard';
import GraphCard from '../../../../../../../../common/Styleguide/Common/GraphCard';
import Play from '../../../../../../../../common/icons/Play';
import Users from '../../../../../../../../common/icons/Users';
import Eye from '../../../../../../../../common/icons/Eye';
import Clicker from '../../../../../../../../common/icons/Clicker';
import Clock from '../../../../../../../../common/icons/Clock';
import Day7 from '../../../../../../../../common/icons/Day7';
import Day1 from '../../../../../../../../common/icons/Day1';
import Users2 from '../../../../../../../../common/icons/User2';
import Star from '../../../../../../../../common/icons/Star';
import Refresh from '../../../../../../../../common/icons/Refresh';
import Feedback from '../../../../../../../../common/icons/Feedback';
import Money from '../../../../../../../../common/icons/Money';
import { Box } from '@mui/system';
import SubmissionTabMetricsBasicLaunch from './SubmissionTabMetricsBasicLaunch';
import { submissionTabMetricsTabsStyle, submissionTabMetricsTabStyle } from './submissionTabMetrics.styles';
import DeviceFilter from './Filters/DeviceFilter';
import TimeframeFilter from './Filters/TimeframeFilter';
import { convertPeriodToPreviousPeriodOption } from '../../../../../../../../common/datetime';

const DAILY_RATING_HISTORICAL_LIMIT_DAY = moment('2023-03-15');
const BASIC_LAUNCH_STATS_AVAILABLE_FROM = '2025-09-01';
const SHOW_BASIC_LAUNCH_STATS_AFTER_DAYS = 1;

type SubmissionTabMetricsProps = {
  submission: Submission; // passing submission to prevent warning on using useQuery conditionally
};

const SubmissionTabMetrics: React.FC<SubmissionTabMetricsProps> = ({ submission }) => {
  const [selectedTab, setSelectedTab] = useState<MetricsCategories>(
    setDefaultSelectedTab(submission.basicLaunchOn, submission.basicLaunchExpiresOn),
  );
  const [timeframeFilterValue, setTimeframeFilterValue] = useState<MetricsTimeframeInput>(DEFAULT_TIMEFRAME_FILTER);
  const [deviceFilterValue, setDeviceFilterValue] = useState<DeviceFilterOptionsValues>('all');
  const [selectedMetric, setSelectedMetric] = useState<MetricsIds | undefined>(undefined);
  const graphRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const handleChange = (_event: React.SyntheticEvent, newValue: MetricsCategories) => {
    setSelectedTab(newValue);
  };

  // LOAD DATA
  const { loading: loadingTotal, data: total } = useQuery<MyGameStatsQuery>(MY_GAME_STATS_QUERY, {
    variables: {
      slug: submission.slug,
      period: timeframeFilterValue.period,
      options: timeframeFilterValue.options,
      device: deviceFilterValue === 'all' ? undefined : deviceFilterValue,
    },
  });
  const { data: previousPeriodTotal } = useQuery<MyGameStatsQuery>(MY_GAME_STATS_QUERY, {
    variables: {
      slug: submission.slug,
      period: 'CUSTOM',
      options: convertPeriodToPreviousPeriodOption(timeframeFilterValue),
      device: deviceFilterValue === 'all' ? undefined : deviceFilterValue,
    },
    skip: timeframeFilterValue.period === 'ALL_TIME',
  });
  const { loading: loadingGraphData, data: rawGraphData } = useQuery<MyGameDailyStatsQuery>(MY_GAME_DAILY_STATS_QUERY, {
    variables: {
      slug: submission.slug,
      options: timeframeFilterValue.options,
      device: deviceFilterValue === 'all' ? undefined : deviceFilterValue,
    },
  });

  const totalData = total && getAggregatedAnalytics(total, previousPeriodTotal, timeframeFilterValue);
  const graphLabels =
    timeframeFilterValue.options && timeframeFilterValue.options.until
      ? getDailyLabels(timeframeFilterValue.options.from, timeframeFilterValue.options.until)
      : undefined;
  const graphData = graphLabels && getDailyRawData(rawGraphData, graphLabels);

  const METRICS_TABS: MetricsTabs[] = [
    {
      isVisible:
        Boolean(submission.basicLaunchOn) &&
        isAfterDate(submission.basicLaunchOn, BASIC_LAUNCH_STATS_AVAILABLE_FROM) &&
        Boolean(submission.basicLaunchExpiresOn) &&
        isPastByNDays(submission.basicLaunchOn, SHOW_BASIC_LAUNCH_STATS_AFTER_DAYS),
      id: 'basic_launch',
      label: 'Basic Launch Results',
      metrics: [],
      filters: {
        device: false,
        time: false,
      },
    },
    {
      isVisible: true,
      id: 'engagement',
      label: 'Engagement',
      metrics: [
        {
          id: 'pageviews',
          showGraphCard: true,
          name: 'Total Plays',
          icon: <Play />,
          tooltip: 'Total number of plays of the game, counting every time a game is loaded succesfully.',
          description: `Total plays in this period and for ${renderDeviceFilterLabelHelper(deviceFilterValue)}.`,
          total: totalData?.totals.selectedData.pageviews,
          totalFormatting: 'amount',
          delta: totalData?.totals.deltas.pageviews,
          deltaFormatting: 'amount',
          graphData: graphData?.pageviews,
        },
        {
          id: 'users',
          showGraphCard: true,
          name: 'Total Players',
          icon: <Users />,
          tooltip: 'The amount of distinct players playing the game (we link logged in users using multiple devices).',
          description: `Total players in this period and for ${renderDeviceFilterLabelHelper(deviceFilterValue)}.`,
          total: totalData?.totals.selectedData.users,
          totalFormatting: 'amount',
          delta: totalData?.totals.deltas.users,
          deltaFormatting: 'amount',
          graphData: graphData?.users,
        },
        {
          id: 'impressions',
          showGraphCard: true,
          name: 'Impressions',
          icon: <Eye />,
          tooltip: "Number of users who viewed the game's thumbnail across the platform.",
          description: `Total impressions in this period and for ${renderDeviceFilterLabelHelper(deviceFilterValue)}.`,
          total: totalData?.totals.selectedData.impressions,
          totalFormatting: 'amount_compact',
          delta: totalData?.totals.deltas.impressions,
          deltaFormatting: 'amount_compact',
          graphData: graphData?.impressions,
        },
        {
          id: 'ctr',
          showGraphCard: true,
          name: 'CTR',
          icon: <Clicker />,
          tooltip: 'Share of users who have clicked on the thumbnail somewhere on the platform.',
          description: `Average clickthrough rate in this period and for ${renderDeviceFilterLabelHelper(deviceFilterValue)}.`,
          total: totalData?.totals.selectedData.ctr,
          totalFormatting: '.0%',
          delta: totalData?.totals.deltas.ctr,
          deltaFormatting: '.0%',
          graphData: graphData?.ctr,
        },
      ],
      filters: {
        device: true,
        time: true,
      },
    },
    {
      isVisible: true,
      id: 'performance',
      label: 'Performance',
      metrics: [
        {
          id: 'avgTimeOnPage',
          showGraphCard: true,
          name: 'Avg Playtime',
          icon: <Clock />,
          tooltip: 'Average time played per play, excluding time away from the game (counted in minutes and seconds).',
          description: `Average playtime in this period and for ${renderDeviceFilterLabelHelper(deviceFilterValue)} (mm:ss).`,
          total: totalData?.totals.selectedData.avgTimeOnPage,
          totalFormatting: 's_to_00:00',
          delta: totalData?.totals.deltas.avgTimeOnPage,
          deltaFormatting: 's_to_00:00',
          graphData: graphData?.avgTimeOnPage,
          graphDataFormatting: 's_to_00:00',
        },
        {
          id: 'avgConversion',
          showGraphCard: true,
          name: 'Gameplay Conversion',
          icon: <Users2 />,
          tooltip:
            "Ratio of plays with a duration of at least 1 min compared to the clicks on 'Play Now' button.<br>Pro Tip: Reduce the initial loading size and simplify the flow to start gameplay in order to improve this ratio and lose fewer players early.",
          description: `Average gameplay conversion in this period and for ${renderDeviceFilterLabelHelper(deviceFilterValue)}.`,
          total: totalData?.totals.selectedData.avgConversion,
          totalFormatting: '%',
          delta: totalData?.totals.deltas.avgConversion,
          deltaFormatting: '.00%',
          graphData: graphData?.avgConversion,
        },
        {
          id: 'retentionD1',
          showGraphCard: true,
          name: 'Retention D1',
          icon: <Day1 />,
          tooltip: 'The ratio of users that play the game again 1 day after they played it for the first time.',
          description: `Average retention (D1) in this period and for ${renderDeviceFilterLabelHelper(deviceFilterValue)}.`,
          total: totalData?.totals.selectedData.retentionD1,
          totalFormatting: '.00%',
          delta: totalData?.totals.deltas.retentionD1,
          deltaFormatting: '.00%',
          graphData: graphData?.retentionD1,
        },
        {
          id: 'retentionD7',
          showGraphCard: true,
          name: 'Retention D7',
          icon: <Day7 />,
          tooltip: 'The ratio of users that play the game again 7 days after they played it for the first time.',
          description: `Average retention (D7) in this period and for ${renderDeviceFilterLabelHelper(deviceFilterValue)}.`,
          total: totalData?.totals.selectedData.retentionD7,
          totalFormatting: '.00%',
          delta: totalData?.totals.deltas.retentionD7,
          deltaFormatting: '.00%',
          graphData: graphData?.retentionD7,
        },
        {
          id: 'returningPlayers',
          showGraphCard: true,
          name: 'Returning Users %',
          icon: <Refresh />,
          tooltip: 'Ratio of players that played the game any day before.',
          description: `Average returning users % in this period and for ${renderDeviceFilterLabelHelper(deviceFilterValue)}.`,
          total: totalData?.totals.selectedData.returningPlayers,
          totalFormatting: '%',
          delta: totalData?.totals.deltas.returningPlayers,
          deltaFormatting: '.00%',
          graphData: graphData?.returningPlayers,
        },
        {
          id: 'rating',
          showGraphCard: false,
          name: 'Rating (+Count)',
          icon: <Star />,
          tooltip: `The amount of user likes/dislikes and the corresponding rating. We don't provide time-based data for this metric.<br>${submission.game?.addedOn && moment.utc(submission.game.addedOn).isBefore(moment.utc(DAILY_RATING_HISTORICAL_LIMIT_DAY)) ? `The rating data starts only from ${DAILY_RATING_HISTORICAL_LIMIT_DAY.format('DD/MM/YYYY')}. Any likes/dislikes before this date are not included.` : ``}<br>This metric is not affected by the device filter.`,
          description: `This rating shows how players have scored the game over the past 6 months, giving you a recent view of its popularity and quality.`,
          total: totalData?.totals.selectedData.rating,
          totalFormatting: '.0',
          count: total?.gameBySlug.devStats.analytics.totalVotes,
          countFormatting: 'amount_compact',
          graphData: graphData?.rating,
        },
        {
          id: 'avgLoadingTime',
          showGraphCard: true,
          name: 'Avg Loading Time',
          icon: <Clock />,
          tooltip: 'Average time it takes between “Play now” click and the start of a play (excluding possible ad or in-game loading).',
          description: `Average loading time in this period and for ${renderDeviceFilterLabelHelper(deviceFilterValue)}.`,
          total: totalData?.totals.selectedData.avgLoadingTime,
          totalFormatting: '0_sec',
          delta: totalData?.totals.deltas.avgLoadingTime,
          deltaFormatting: '0_sec', // NEGATIVE IS GOOD IN THIS CASE
          graphData: graphData?.avgLoadingTime,
        },
        {
          id: 'issues',
          showGraphCard: true,
          name: 'Errors / 1K plays',
          icon: <Feedback />,
          tooltip: 'Amount of issues per 1000 plays of the game. This includes progress save issues and breaking Unity errors.',
          description: `Average errors per 1k plays in this period and for ${renderDeviceFilterLabelHelper(deviceFilterValue)}.`,
          total: totalData?.totals.selectedData.issues,
          totalFormatting: 'amount',
          delta: totalData?.totals.deltas.issues,
          deltaFormatting: 'amount',
          graphData: graphData?.issues,
        },
      ],
      filters: {
        device: true,
        time: true,
      },
    },
    {
      isVisible: true,
      id: 'monetization',
      label: 'Monetization',
      metrics: [
        {
          id: 'revenue',
          showGraphCard: true,
          name: 'Total Revenue',
          icon: <Money />,
          tooltip: 'The revenue the game has generated for you.',
          description: `Total revenue in the selected period.`,
          graphDataFormatting: '€',
          total: totalData?.totals.selectedData.revenue,
          totalFormatting: '€',
          delta: totalData?.totals.deltas.revenue,
          deltaFormatting: '€',
          graphData: graphData?.revenue,
        },
        {
          id: 'revenuePerThousandPlays',
          showGraphCard: true,
          name: 'Revenue / 1K plays',
          icon: <Money />,
          tooltip: 'The revenue the game has generated for you.',
          description: `Average revenue per 1k plays in this period and for all devices.`,
          total: totalData?.totals.selectedData.revenuePerThousandPlays,
          totalFormatting: '€',
          delta: totalData?.totals.deltas.revenuePerThousandPlays,
          deltaFormatting: '€',
          graphData: graphData?.revenuePerThousandPlays,
          graphDataFormatting: '€',
        },
      ],
      filters: {
        device: false,
        time: true,
      },
      notes:
        graphData?.iap && sumArrayValues(graphData.iap) ? (
          <>
            <p>
              This chart includes both ad revenue and in-game purchase revenue. Please note that due to Xsolla's daily exchange rate
              updates, updates, in-game purchase revenue may fluctuate in the daily data. Therefore, we recommend comparing the full month's
              receiving the self-billing invoice, which reflects the final, fixed amount following Xsolla's monthly closing.
            </p>
            <p>
              Additionally, minor discrepancies may occur between the Xsolla UI (which uses UTC+3) and the Developer Portal (which uses UTC)
              due to timezone differences. If you are matching revenue figures between these platforms, make sure to account for this
              timezone shift to ensure accurate comparisons.
            </p>
          </>
        ) : undefined,
    },
  ];

  return (
    <>
      <Tabs value={selectedTab} onChange={handleChange} sx={submissionTabMetricsTabsStyle}>
        {METRICS_TABS.map((tab, i) => {
          if (!tab.isVisible) {
            return null;
          }
          return <Tab key={i} value={tab.id} label={tab.label} sx={submissionTabMetricsTabStyle} />;
        })}
      </Tabs>

      {selectedTab !== 'basic_launch' && (
        <>
          <Notification
            theme="info"
            description={
              <Typography variant="body2">
                Stats are updated <strong>daily</strong>.
              </Typography>
            }
            extraSx={{ my: '20px' }}
          />
          <div style={{ display: 'flex', alignItems: 'center', columnGap: '16px' }}>
            {selectedTab !== 'monetization' && <DeviceFilter value={deviceFilterValue} setValue={setDeviceFilterValue} />}
            <TimeframeFilter value={timeframeFilterValue} setValue={setTimeframeFilterValue} showLabel={false} />
          </div>
        </>
      )}

      {METRICS_TABS.map((tab, i) => {
        if (!tab.isVisible) {
          return null;
        }

        if (tab.id === 'basic_launch' && tab.id === selectedTab) {
          return (
            <SubmissionTabMetricsBasicLaunch
              key={i}
              slug={submission.slug}
              categoryName={submission.categoryName}
              basicLaunchOn={submission.basicLaunchOn}
              basicLaunchExpiresOn={submission.basicLaunchExpiresOn}
            />
          );
        }

        return (
          <div
            key={i}
            role="tabpanel"
            hidden={tab.id !== selectedTab}
            id={`simple-tabpanel-${tab.id}`}
            aria-labelledby={`simple-tab-${tab.id}`}
          >
            <MetricCards>
              {tab.metrics.map((metric, i) => {
                return (
                  <MetricCard
                    key={i}
                    id={metric.id}
                    icon={metric.icon}
                    name={metric.name}
                    tooltip={metric.tooltip}
                    loading={loadingTotal}
                    total={metric.total}
                    totalFormatting={metric.totalFormatting}
                    count={metric.count}
                    countFormatting={metric.countFormatting}
                    isAnchor={graphRefs.current[metric.id]}
                    setSelectedMetric={setSelectedMetric}
                    selectedMetric={selectedMetric}
                  />
                );
              })}
            </MetricCards>
            <GraphCards>
              {tab.metrics.map((metric, i) => {
                const isSelectedMetric = metric.id === selectedMetric;

                if (!metric.showGraphCard) {
                  return null;
                }

                return (
                  <GraphCard
                    key={i}
                    ref={(el) => (graphRefs.current[metric.id] = el)}
                    id={metric.id}
                    name={metric.name}
                    loadingTotal={loadingTotal}
                    total={metric.total}
                    totalFormatting={metric.totalFormatting}
                    description={metric.description}
                    loadingGraphData={loadingGraphData}
                    delta={metric.delta}
                    deltaFormatting={metric.deltaFormatting}
                    deltaLabel={totalData?.helpers.deltaLabel}
                    graphData={metric.graphData}
                    graphLabels={graphLabels}
                    graphNotes={metric.graphNotes}
                    isSelectedMetric={isSelectedMetric}
                    graphDataFormatting={metric.graphDataFormatting}
                  />
                );
              })}
            </GraphCards>

            {tab.notes && <Box sx={{ mt: 5 }}>{tab.notes}</Box>}
          </div>
        );
      })}
    </>
  );
};

export default SubmissionTabMetrics;
